import { useCallback, useEffect, useRef, useState } from "react";

const KEY = "aikyam_presenter_fit";
const ZKEY = "aikyam_presenter_zoom";
const MIN = 0.65;
const MAX = 1.35;

/** Presenter view: scales the page down just enough that a whole screen fits the viewport,
 *  so nothing has to be scrolled while presenting. Manual nudges override the auto fit
 *  until the presenter asks for auto again. The choice survives reloads. */
export const useAutoFit = () => {
  const ref = useRef(null);
  const [on, setOn] = useState(() => localStorage.getItem(KEY) === "1");
  const [zoom, setZoom] = useState(1);
  const [manual, setManual] = useState(() => {
    const stored = Number(localStorage.getItem(ZKEY));
    return stored >= MIN && stored <= MAX ? stored : null;
  });

  useEffect(() => { localStorage.setItem(KEY, on ? "1" : "0"); }, [on]);
  useEffect(() => {
    if (manual == null) localStorage.removeItem(ZKEY);
    else localStorage.setItem(ZKEY, String(manual));
  }, [manual]);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el || !on || manual != null) return;
    const rect = el.getBoundingClientRect();
    if (rect.height <= 0 || rect.width <= 0) return;
    // rect is in real screen pixels, so scale the current zoom by how much room is left
    const available = window.innerHeight - rect.top - 14;
    if (available <= 0) return;
    const z = zoom || 1;
    let next = z * available / rect.height;
    // never scale up past the point where the content would need horizontal scrolling
    const widthCap = z * (window.innerWidth - rect.left - 24) / rect.width;
    next = Math.min(next, Math.max(widthCap, MIN));
    next = Math.min(MAX, Math.max(MIN, Math.round(next * 100) / 100));
    setZoom((prev) => (Math.abs(prev - next) > 0.015 ? next : prev));
  }, [on, manual, zoom]);

  useEffect(() => {
    if (!on) { setZoom(1); return undefined; }
    if (manual != null) { setZoom(manual); return undefined; }
    const raf = requestAnimationFrame(measure);
    const t = setTimeout(measure, 350);
    const ro = new ResizeObserver(() => measure());
    if (ref.current) ro.observe(ref.current);
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [on, manual, measure]);

  return {
    ref,
    on,
    zoom,
    isManual: manual != null,
    toggle: () => { setManual(null); setOn((v) => !v); },
    nudge: (delta) => setManual((m) => {
      const base = m ?? zoom;
      return Math.min(MAX, Math.max(MIN, Math.round((base + delta) * 100) / 100));
    }),
    autoFit: () => setManual(null),
  };
};
